/** Small shared pieces for the Lead Command Center demo views (/demo/lcc/*). */

import { AlertCircle, type LucideIcon } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import type { LccLead } from '@/lib/lcc-api';

export function LccLoadingCard() {
  return <Card className="h-[320px] animate-pulse" />;
}

export function LccErrorCard({ title, error }: { title: string; error: unknown }) {
  return (
    <Card className="p-6">
      <div className="flex items-center gap-2 text-destructive">
        <AlertCircle className="h-4 w-4" aria-hidden />
        <h2 className="text-base font-semibold">{title}</h2>
      </div>
      <p className="mt-1 text-sm text-muted-foreground">
        {error instanceof Error ? error.message : 'Unknown error'}
      </p>
    </Card>
  );
}

export function LccPageHeader({ title, blurb }: { title: string; blurb: string }) {
  return (
    <header className="space-y-1">
      <h1 className="text-[20px] font-semibold leading-tight">{title}</h1>
      <p className="max-w-[75ch] text-[13px] leading-snug text-muted-foreground">{blurb}</p>
    </header>
  );
}

export function formatLccLabel(value: string): string {
  return value.replaceAll('_', ' ');
}

export function formatLccDateTime(value: string | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value.slice(0, 10) : date.toLocaleString();
}

/** "2h ago" / "3d ago" / "in 4h" — compact relative time for list rows. */
export function formatLccRelative(value: string | undefined): string {
  if (!value) return '—';
  const ts = Date.parse(value);
  if (Number.isNaN(ts)) return value.slice(0, 10);
  const diffMs = Date.now() - ts;
  const future = diffMs < 0;
  const abs = Math.abs(diffMs);
  const minutes = Math.round(abs / 60_000);
  const label =
    minutes < 60
      ? `${Math.max(minutes, 1)}m`
      : minutes < 60 * 24
        ? `${Math.round(minutes / 60)}h`
        : `${Math.round(minutes / (60 * 24))}d`;
  return future ? `in ${label}` : `${label} ago`;
}

// ---------- status / priority presentation ----------

type BadgeVariant = 'default' | 'secondary' | 'outline' | 'success' | 'warning' | 'destructive' | 'info' | 'muted';

const STATUS_VARIANTS: Record<string, BadgeVariant> = {
  new: 'info',
  needs_review: 'warning',
  ready_for_verification: 'default',
  verified: 'success',
  qualified: 'success',
  callback_needed: 'warning',
  waiting_on_client: 'muted',
  appointment_set: 'info',
  estimate_sent: 'default',
  converted: 'success',
  lost: 'destructive',
  recycle_later: 'muted',
  spam: 'destructive',
};

export function LccStatusBadge({ status, label }: { status: string; label?: string }) {
  return (
    <Badge variant={STATUS_VARIANTS[status] ?? 'secondary'} className="whitespace-nowrap">
      {label ?? formatLccLabel(status)}
    </Badge>
  );
}

// ---------- due state (the lead's own return-to-LeadLoop timer) ----------

const LCC_CLOSED_STATUSES = ['converted', 'lost', 'spam', 'recycle_later'];

export interface LccDueState {
  kind: 'overdue' | 'due_now' | 'snoozed' | 'needs_next_step' | 'closed';
  label: string;
}

/**
 * User-friendly reading of leads.next_follow_up_at: Due now / Overdue /
 * Snoozed until <date> / Needs next step. Mirrors the Worker's LeadLoop
 * eligibility so what the badge says matches whether the lead is in the loop.
 */
export function leadDueState(lead: LccLead, now: Date = new Date()): LccDueState {
  if (LCC_CLOSED_STATUSES.includes(lead.status)) {
    return { kind: 'closed', label: '' };
  }
  if (lead.nextFollowUpAt) {
    const ts = Date.parse(lead.nextFollowUpAt);
    if (ts < now.getTime()) {
      return { kind: 'overdue', label: `Overdue (${formatLccRelative(lead.nextFollowUpAt)})` };
    }
    const endOfToday = new Date(now);
    endOfToday.setHours(23, 59, 59, 999);
    if (ts <= endOfToday.getTime()) {
      return { kind: 'due_now', label: 'Due now' };
    }
    const due = new Date(ts);
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    if (due.toDateString() === tomorrow.toDateString()) {
      return { kind: 'snoozed', label: 'Follow up tomorrow' };
    }
    return {
      kind: 'snoozed',
      label: `Snoozed until ${due.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`,
    };
  }
  return { kind: 'needs_next_step', label: 'Needs next step' };
}

const DUE_BADGE_VARIANTS: Record<LccDueState['kind'], BadgeVariant> = {
  overdue: 'destructive',
  due_now: 'info',
  snoozed: 'muted',
  needs_next_step: 'warning',
  closed: 'muted',
};

export function LccDueBadge({ lead }: { lead: LccLead }) {
  const state = leadDueState(lead);
  if (state.kind === 'closed') return null;
  return (
    <Badge variant={DUE_BADGE_VARIANTS[state.kind]} className="whitespace-nowrap">
      {state.label}
    </Badge>
  );
}

/** The one thing a human should do next for this lead. */
export function nextActionForLead(lead: LccLead): string {
  if (lead.nextStep) return lead.nextStep;
  if (lead.nextFollowUpAt && Date.parse(lead.nextFollowUpAt) < Date.now()) {
    return 'Follow up — this lead is overdue';
  }
  switch (lead.status) {
    case 'new':
      return 'Make first contact';
    case 'needs_review':
      return 'Review & qualify';
    case 'ready_for_verification':
      return 'Verify this lead';
    case 'callback_needed':
      return lead.phone ? `Call back ${lead.contactName ?? 'the lead'}` : 'Get a phone number';
    case 'waiting_on_client':
      return 'Nudge the client';
    case 'verified':
    case 'qualified':
      return 'Send proposal / book next step';
    case 'appointment_set':
      return 'Prep for the appointment';
    case 'estimate_sent':
      return 'Follow up on the estimate';
    default:
      return 'Set the next step';
  }
}

export function LccEmptyState({
  icon: Icon,
  message,
}: {
  icon: LucideIcon;
  message: string;
}) {
  return (
    <div className="flex flex-col items-center gap-2 px-4 py-12 text-center text-sm text-muted-foreground">
      <Icon className="h-6 w-6 text-muted-foreground/50" aria-hidden />
      <span>{message}</span>
    </div>
  );
}
